import {
  IconButton,
  Input,
  ISelectOption,
  Select,
  Text,
  theme,
  Icon,
  Tooltip,
  Spinner,
  Modal,
} from '@salecycle/ui-component-library';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { State } from 'store';
import {
  updateTemplateId,
  updateInjectedTemplatePosition,
  updateInjectedTemplateSelector,
} from 'entities/campaign/rulesets/channel-patcher';
import Site from 'sections/client-management/entities/site';
import { Selectors as SiteSelectors } from 'sections/client-management/services/sites';
import { Selectors as Router } from 'router';
import {
  isOffSiteChannel,
  TemplateChannel,
  isInjectedChannel,
  isOnSiteChannel,
} from 'entities/campaign/rulesets/channels';
import { TemplatePosition, TemplatePositionDescriptions } from 'entities/campaign/rulesets/template-position';
import { OnsiteTypes, Template as TemplateEntity } from 'entities/template';
import { Selectors as TemplateSelectors } from 'sections/assets/state/templates';
import { Actions as RulesetActions } from 'sections/campaigns/state/rulesets';
import { DispatchProps } from './props';
import Block from '../components/Block';
import { useFeatureFlags, usePermissions } from 'core/app/AppContext';
import { TemplateEmailPreview } from './TemplateEmailPreview';
import { useTemplates } from '../../../data/CampaignsApi';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { DelaySelector } from './DelaySelector';
import { getInitialValues, getDelay } from '../../../../../utils/timeUtils';
import { maximumDelay } from '../configs/sendConfig';
import { useQuery } from 'react-query';
import {
  getMPSEmbedTemplates,
  getMPSPopupTemplates,
} from 'sections/design/template-management/sections/template-list/utils/queries';
import { ITemplate } from 'entities/template/template';

const Loading = styled.div`
  background-color: ${({ theme }) => theme.vars.newColor.white};
  position: relative;
  margin-bottom: 1.6rem;
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
`;

const PreviewLink = styled(Link)`
  border: 1px solid transparent;
  color: ${({ theme }) => theme.vars.newColor.darkGS2};
  padding: 0.4rem 0.7rem;

  &:hover {
    background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
    border-color: ${({ theme }) => theme.vars.newColor.lightGS1};
  }
`;

const List = styled.ul`
  list-style-type: none;
  margin-bottom: 0;
`;

const Item = styled.li``;

const Title = styled.span`
  font-weight: 600;
`;

interface IFormProps extends DispatchProps {
  channel: TemplateChannel;
  rulesetId: string;
  channelIndex: number;
  templates: TemplateEntity[];
}

interface OwnProps {
  allowChannelScheduling?: boolean;
  bubbleDisabled?: boolean;
  channelIndex: number;
  rulesetId: string;
  channel: TemplateChannel;
  campaignState: CampaignState;
  siteId?: string;
}

interface StateProps {
  templates: TemplateEntity[];
  site: Site;
}

interface TemplateDispatchProps {
  deleteChannel: (rulesetId: string, channelIndex: number, cycleId: string) => void;
}

export type Props = OwnProps & StateProps & DispatchProps & TemplateDispatchProps;

const useMPSTemplates = (siteId: string, t: any) => {
  const [mpsOptions, setMpsOptions] = useState([]);
  const mpsEmbedQuery = useQuery(['mpsEmbed'], () => getMPSEmbedTemplates({ siteId, t }));
  const mpsPopupQuery = useQuery(['mpsPopup'], () => getMPSPopupTemplates({ siteId, t }));

  useEffect(() => {
    if (mpsEmbedQuery.isSuccess && mpsPopupQuery.isSuccess) {
      const embedOptions = mapTemplatesToOptions(mpsEmbedQuery.data, OnsiteTypes.Injected);
      const popupOptions = mapTemplatesToOptions(mpsPopupQuery.data, OnsiteTypes.Message);
      setMpsOptions(embedOptions.concat(popupOptions));
    }
  }, [mpsEmbedQuery.data, mpsEmbedQuery.isSuccess, mpsPopupQuery.data, mpsPopupQuery.isSuccess]);

  const mapTemplatesToOptions = (templates: ITemplate[], type: OnsiteTypes) => {
    return templates.map(template => ({
      id: template.id,
      name: template.templateName,
      type,
      label: template.templateName,
      value: template.id,
    }));
  };

  return mpsOptions;
};

export const Form: FC<IFormProps> = ({ channel, templates, channelIndex, rulesetId, update }) => {
  const { t } = useTranslation('campaigns');
  const { flagEnabled } = useFeatureFlags();
  const mpsFeature = flagEnabled('ld-mps-template');

  const { siteId } = useParams<{ siteId: string }>();
  const getTemplateOptions: ISelectOption<string>[] = useMPSTemplates(siteId, t);

  const templateOptions: ISelectOption<string>[] = mpsFeature
    ? getTemplateOptions
    : templates.map(template => ({
        value: template.id,
        label: isOnSiteChannel(channel) ? `${template.name} (${template.id})` : `${template.name}`,
      }));

  const positionOptions: ISelectOption<string>[] = Object.keys(TemplatePosition).map(key => ({
    label: t(TemplatePositionDescriptions[key]),
    value: TemplatePosition[key],
  }));

  const templateValue = templateOptions.find(temp => temp.value === channel.templateId) || null;

  const positionValue = isInjectedChannel(channel)
    ? positionOptions.find(pos => pos.value === channel.templateInjectedPosition) || null
    : null;

  const onOnSiteTemplateChange = (option: ISelectOption<string>): void => {
    let templateObject;
    if (mpsFeature) {
      templateObject = templateOptions.find((template): boolean => template.value === option.value);
    } else {
      templateObject = templates.find((template): boolean => template.id === option.value);
    }
    update(rulesetId, channelIndex, updateTemplateId(channel, templateObject));
  };

  const onPositionChange = (option: ISelectOption<string>): void => {
    if (isInjectedChannel(channel)) {
      update(rulesetId, channelIndex, updateInjectedTemplatePosition(channel, option.value as TemplatePosition));
    }
  };

  const onSelectorChange = (value: string): void => {
    if (isInjectedChannel(channel)) {
      update(rulesetId, channelIndex, updateInjectedTemplateSelector(channel, value));
    }
  };

  return (
    <>
      <Select
        ariaLabel={t('campaigns:editor.rulesets.channels.template.templateIdLabelOnsite')}
        label={t('campaigns:editor.rulesets.channels.template.templateIdLabelOnsite')}
        value={templateValue}
        options={templateOptions}
        onChange={onOnSiteTemplateChange}
        clearable={false}
        validationState={!!channel.templateId ? 'complete' : 'warning'}
      />
      {isInjectedChannel(channel) && (
        <>
          <Text>{t('campaigns:editor.rulesets.channels.template.injected.description')}</Text>
          <Input
            ariaLabel={t('campaigns:editor.rulesets.channels.template.injected.templateSelectorAriaLabel')}
            label={t('campaigns:editor.rulesets.channels.template.injected.templateSelectorLabel')}
            placeholder="selector"
            onChange={onSelectorChange}
            value={channel.selector}
            validationState={!!channel.selector ? 'complete' : 'warning'}
          />
          <Select
            ariaLabel={t('campaigns:editor.rulesets.channels.template.injected.templatePositionLabel')}
            label={t('campaigns:editor.rulesets.channels.template.injected.templatePositionLabel')}
            value={positionValue}
            options={positionOptions}
            onChange={onPositionChange}
            clearable={false}
            validationState={!!channel.templateInjectedPosition ? 'complete' : 'warning'}
            data-testid="templateposition"
          />
        </>
      )}
    </>
  );
};

export const Template: FC<Props> = props => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { flagEnabled } = useFeatureFlags();
  const mpsFeature = flagEnabled('ld-mps-template');

  const { siteId } = useParams<{ siteId: string }>();
  const getTemplateOptions = useMPSTemplates(siteId, t);

  const { templates: emailTemplates, status } = useTemplates(props.channel.type === '.Email', props.siteId);
  const mappedEmailTemplates: TemplateEntity[] = emailTemplates.map(template => ({
    id: template.id,
    name: template.templateName,
    type: template.channel,
  }));

  const templates = props.channel.type === '.Email' ? mappedEmailTemplates : props.templates;

  const offSiteTemplateOptions = templates.map(template => ({
    value: template.id,
    label: `${template.name}`,
  }));
  const offSiteTemplate = offSiteTemplateOptions.find(t => t.value === props.channel.templateId) || null;
  const [tempOffSiteTemplate, setOffSiteTemplate] = useState(offSiteTemplate);
  const onOffSiteTemplateChange = (option: ISelectOption<string>): void => {
    const templateObject = offSiteTemplateOptions.find(temp => temp.value === option.value);
    setOffSiteTemplate(templateObject);
  };

  const channelDelay = isOffSiteChannel(props.channel) ? props.channel.delay : 0;
  const [tempDelay, setDelay] = useState(channelDelay);
  const onDelayChange = (howMany: number, unit: string) => {
    setDelay(getDelay(howMany, unit));
  };

  const subjectLine = isOffSiteChannel(props.channel) ? props.channel.subjectLine : '';
  const [tempSubjectLine, setSubjectLine] = useState(subjectLine);
  const updateSubjectLine = (value: string): void => {
    setSubjectLine(value);
  };

  const [offSiteModalOpen, setOffSiteModalOpen] = useState(false);

  const showOffSiteModal = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    const subjectLine = isOffSiteChannel(props.channel) ? props.channel.subjectLine : '';
    setSubjectLine(subjectLine);
    const offSiteTemplate = offSiteTemplateOptions.find(t => t.value === props.channel.templateId) || null;
    setOffSiteTemplate(offSiteTemplate);
    const channelDelay = isOffSiteChannel(props.channel) ? props.channel.delay : 0;
    setDelay(channelDelay);
    setOffSiteModalOpen(true);
  };
  const saveAndCloseOffSiteModal = () => {
    if (isOffSiteChannel(props.channel)) {
      props.channel.delay = tempDelay;
      props.channel.subjectLine = tempSubjectLine;
      props.channel.templateId = tempOffSiteTemplate?.value;
      setOffSiteModalOpen(false);
      props.update(props.rulesetId, props.channelIndex, props.channel);
    }
  };

  const CancelAndOffSiteCloseModal = () => {
    setOffSiteModalOpen(false);
  };

  const deleteChannel = () => {
    let cycleId = undefined;
    if (isOffSiteChannel(props.channel)) {
      cycleId = props.channel.cycleId;
    }
    props.deleteChannel(props.rulesetId, props.channelIndex, cycleId);
  };

  const isValid = isInjectedChannel(props.channel)
    ? !!props.channel.selector && !!props.channel.templateInjectedPosition && !!props.channel.templateId
    : !!props.channel.templateId;

  const isEmailPreviewEnabled = isOffSiteChannel(props.channel) && props.channel.templateId !== null;

  const templateName: string = mpsFeature
    ? getTemplateOptions.find(template => template.id === props.channel.templateId)?.name ?? props.channel.templateId
    : templates.find(template => template.id === props.channel.templateId)?.name ?? props.channel.templateId;

  const canDelete = props.allowChannelScheduling && isOffSiteChannel(props.channel);

  const sessionTime = props.site?.sessionTime ? props.site.sessionTime : 0;
  if (status === 'idle' || status === 'pending') {
    return (
      <Loading>
        <Spinner />
      </Loading>
    );
  }

  return (
    <div>
      {isOffSiteChannel(props.channel) && (
        <Modal
          variation="clean"
          isOpen={offSiteModalOpen}
          heading={t('campaigns:editor.rulesets.offSiteChannelModal.heading')}
          okButtonLabel={t('campaigns:editor.rulesets.offSiteChannelModal.okButton.label')}
          onOkButtonClicked={saveAndCloseOffSiteModal}
          okButtonDisabled={!tempSubjectLine || tempDelay <= 0 || tempDelay > maximumDelay || !tempOffSiteTemplate}
          cancelButtonType="delete"
          cancelButtonLabel={t('campaigns:editor.rulesets.offSiteChannelModal.cancelButton.label')}
          onCancelButtonClicked={CancelAndOffSiteCloseModal}
          size="large"
        >
          <DelaySelector
            settings={getInitialValues(props.channel.delay < sessionTime ? sessionTime : props.channel.delay)}
            sessionTimeout={getInitialValues(sessionTime)}
            onChange={onDelayChange}
          />
          <Select
            ariaLabel={t('campaigns:editor.rulesets.channels.template.templateIdLabelOffsite')}
            label={t('campaigns:editor.rulesets.channels.template.templateIdLabelOffsite')}
            value={tempOffSiteTemplate}
            options={offSiteTemplateOptions}
            onChange={onOffSiteTemplateChange}
            clearable={false}
            validationState={!!props.channel.templateId ? 'complete' : 'warning'}
          />
          <Input
            ariaLabel={t('campaigns:editor.rulesets.channels.template.templateAriaSubjectLine')}
            label={t('campaigns:editor.rulesets.channels.template.templateSubjectLine')}
            placeholder={t('campaigns:editor.rulesets.channels.template.templateSubjectLinePlaceholder')}
            onChange={updateSubjectLine}
            value={!!tempSubjectLine ? String(tempSubjectLine) : ''}
            validationState={!!tempSubjectLine ? 'complete' : 'warning'}
            validationMessage={
              !tempSubjectLine && t('campaigns:editor.rulesets.channels.template.templateSubjectLineValidationMessage')
            }
            inputTestId="subjectLine"
          />
        </Modal>
      )}
      <Block
        preview={
          isEmailPreviewEnabled && (
            <TemplateEmailPreview
              siteId={props.siteId}
              templateId={props.channel.templateId}
              width={298}
              height={177}
              templateName={templateName}
            />
          )
        }
        icon="envelope"
        iconColor={theme.vars.newColor.blue2}
        campaignState={props.campaignState}
        title={
          isOffSiteChannel(props.channel)
            ? t('campaigns:editor.rulesets.channels.template.titleOffsiteEmail', {
                emailNumber: props.channelIndex + 1,
              })
            : t('campaigns:editor.rulesets.channels.template.titleOnsite')
        }
        value={templateName}
        valid={isValid}
        secondValue={
          isOffSiteChannel(props.channel) &&
          props.channel.delay >= sessionTime &&
          t('campaigns:editor.rulesets.channels.template.scheduled.delayedBody', {
            howMany: getInitialValues(props.channel.delay).howMany,
            units: getInitialValues(props.channel.delay).units,
          })
        }
        controls={
          <>
            {isOffSiteChannel(props.channel) && (
              <>
                <Tooltip
                  title={t('campaigns:editor.rulesets.channels.editOffSiteChannel.iconAriaLabel')}
                  position="top"
                >
                  <IconButton
                    ariaLabel={t('campaigns:editor.rulesets.channels.editOffSiteChannel.iconAriaLabel')}
                    icon="edit"
                    onClick={showOffSiteModal}
                    data-testid="offSite-channel-modal-button"
                  />
                </Tooltip>
              </>
            )}
            {props.allowChannelScheduling && (
              <>
                {isValid && hasPermission('ManageTemplates') && (
                  <Tooltip title={t('campaigns:editor.rulesets.channels.template.preview')} position="top">
                    <PreviewLink
                      to={`/sites/${props.siteId}/designs/templates/${props.channel.templateId}/editor`}
                      target="_blank"
                      aria-describedby={t('campaigns:editor.rulesets.channels.template.preview')}
                      data-testid="TemplatePreview"
                    >
                      <Icon icon="code" />
                    </PreviewLink>
                  </Tooltip>
                )}
                {canDelete && (
                  <>
                    <Tooltip title={t('campaigns:editor.rulesets.channels.delete.iconAriaLabel')} position="top">
                      <IconButton
                        ariaLabel={t('campaigns:editor.rulesets.channels.delete.iconAriaLabel')}
                        icon="trash"
                        onClick={deleteChannel}
                        data-testid="delete-channel-button"
                      />
                    </Tooltip>
                  </>
                )}
              </>
            )}
          </>
        }
        details={
          <>
            {isOffSiteChannel(props.channel) && props.channel.subjectLine && (
              <List>
                <Item data-testid="CampaignEditorDetailsSubjectTitle">
                  <Title>{t('campaigns:editor.rulesets.channels.template.templateSubjectLineTitle')}</Title>
                </Item>
                <Item data-testid="CampaignEditorDetailsSubjectValue">{props.channel.subjectLine}</Item>
              </List>
            )}
          </>
        }
        form={
          <>
            {!props.bubbleDisabled && (
              <Form
                update={props.update}
                channel={props.channel}
                channelIndex={props.channelIndex}
                rulesetId={props.rulesetId}
                templates={templates}
              />
            )}
          </>
        }
        disableOpen={isOffSiteChannel(props.channel)}
      />
    </div>
  );
};

export const mapState = (state: State): StateProps => {
  const siteId = Router.getSiteId(state);
  return {
    templates: TemplateSelectors.getEntities(state),
    site: SiteSelectors.getUpdatedEntity(state, siteId),
  };
};

const actions: DispatchProps & TemplateDispatchProps = {
  deleteChannel: RulesetActions.channelDeleted,
  update: RulesetActions.channelChanged,
};

export default connect(mapState, actions)(Template);
